import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Spinner } from "components";
import { useSession } from "session";
import { APPLICATIONS_KEY } from "pages/Applications/useApplications";
import { DueChip, StatusChip } from "pages/Applications/StatusChip";
import { InviteLinkPanel, useResendInvite } from "pages/Applications/InviteLinkPanel";
import {
  applicationStatus,
  approveBusiness,
  approveReviewItem,
  BusinessApiError,
  dueTone,
  getBusiness,
  holdReviewItem,
  inviteIsOpen,
  itemLabel,
  submittedValuesFor,
  listHoldReasons,
  listPortalProducts,
  notApproveBusiness,
  timeAgo,
  type BusinessDetail,
  type HoldReason,
  type PortalProduct,
  type ReviewItem,
} from "service/business";
import { NotApproveDialog } from "./NotApproveDialog";
import { SendBackDialog } from "./SendBackDialog";
import { ApproveAllDialog } from "./ApproveAllDialog";
import {
  linkButton,
  outlineButton,
  primaryButton,
  smallButton,
  smallSolid,
} from "./styles";

const ITEM_STATUS_WORDS: Record<string, string> = {
  open: "To review",
  approved: "Approved",
  held: "Sent back",
};
const ITEM_STATUS_COLOURS: Record<string, { bg: string; text: string }> = {
  open: { bg: "#E1EBFC", text: "#165FE3" },
  approved: { bg: "#E1FFE2", text: "#14622A" },
  held: { bg: "#FFF1D6", text: "#8A5200" },
};

const messageOf = (e: unknown, fallback = "Something went wrong. Try again.") =>
  e instanceof BusinessApiError ? e.message : fallback;

/** Rows a reviewer decides. Readiness items belong to a later step. */
const isReviewRow = (i: ReviewItem) =>
  i.status !== "superseded" && i.kind !== "readiness" && i.kind !== "registration";

export default function ApplicationReview() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, can } = useSession();
  const mayReview = can("business.review");
  const mayResend = can("business.create");
  const mayOpen = mayReview || mayResend;
  const resender = useResendInvite();
  const approvingAll = useRef(false);

  const { data, isLoading, isError, refetch } = useQuery<BusinessDetail>({
    queryKey: ["business", id],
    queryFn: () => getBusiness(id),
    refetchOnWindowFocus: false,
    enabled: mayOpen,
    retry: 1,
    retryDelay: 500,
  });

  const [reasons, setReasons] = useState<HoldReason[]>([]);
  const [products, setProducts] = useState<PortalProduct[]>([]);
  useEffect(() => {
    listHoldReasons().then(setReasons).catch(() => undefined);
    listPortalProducts().then(setProducts).catch(() => undefined);
  }, []);

  const [busy, setBusy] = useState<string | null>(null);
  const [sendBack, setSendBack] = useState<ReviewItem | null>(null);
  const [sendBackError, setSendBackError] = useState("");
  const [notApproveOpen, setNotApproveOpen] = useState(false);
  const [approveAllOpen, setApproveAllOpen] = useState(false);
  const [notApproveError, setNotApproveError] = useState("");

  const refresh = async () => {
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries([APPLICATIONS_KEY]),
    ]);
  };

  const rows = useMemo(() => (data?.items ?? []).filter(isReviewRow), [data]);
  const docById = (docId: string | null) =>
    data?.documents.find((d) => d.id === docId);

  if (!mayOpen) {
    return (
      <div className="p-6 text-sm" role="alert">
        You don&apos;t have access to applications.{" "}
        <Link to="/dashboard" className={linkButton}>
          Back to Home
        </Link>
      </div>
    );
  }
  if (isLoading) return <Spinner />;
  if (isError || !data) {
    return (
      <div className="p-6 text-sm flex flex-col gap-3" role="alert">
        <Link to="/lead/contacts/corporate?tab=applications" className={`${linkButton} w-fit`}>
          ‹ Applications
        </Link>
        <p>
          Couldn&apos;t load this application.{" "}
          <button type="button" className={linkButton} onClick={() => refetch()}>
            Retry
          </button>
        </p>
      </div>
    );
  }

  const valuesFor = submittedValuesFor(data);
  const invite = data.invites[0];
  const status = applicationStatus(data.state, invite?.status ?? "");
  const inviteOpen = inviteIsOpen(invite?.status);
  const contact = data.people.find((p) => p.isContact) ?? data.people[0];
  const submitted = data.application?.submittedAt ?? null;
  const tone = status === "waiting" ? dueTone(rows.find((r) => r.dueAt)?.dueAt ?? null) : null;
  const productNames = data.products
    .map((p) => products.find((x) => x.slug === p.slug)?.label ?? p.slug)
    .join(", ");

  const decidable = data.state === "submitted" || data.state === "in_review" || data.state === "fix_asked";
  const anyHeld = rows.some((r) => r.status === "held");

  const runItem = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
      await refresh();
    } catch (e) {
      toast.error(messageOf(e));
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  const approveAll = async () => {
    if (approvingAll.current) return;
    approvingAll.current = true;
    setBusy("all");
    try {
      // Decide on what the server holds now, not on what this page loaded earlier.
      const fresh = await getBusiness(id);
      const items = fresh.items.filter(isReviewRow);
      if (items.some((i) => i.status === "held")) {
        toast.error("An item was sent back. Resolve it before approving.");
        return;
      }
      for (const r of items.filter((x) => x.status === "open")) {
        await approveReviewItem(r.id);
      }
      await approveBusiness(id);
      toast.dismiss();
      toast.success("Approved.");
    } catch (e) {
      toast.error(messageOf(e));
    } finally {
      await refresh();
      setBusy(null);
      approvingAll.current = false;
    }
  };

  const confirmSendBack = async (reasonCode: string, reasonNote: string) => {
    if (!sendBack) return;
    setBusy("sendback");
    setSendBackError("");
    try {
      await holdReviewItem(sendBack.id, reasonCode, reasonNote);
      setSendBack(null);
      await refresh();
    } catch (e) {
      setSendBackError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  const confirmNotApprove = async (reason: string) => {
    setBusy("notapprove");
    setNotApproveError("");
    try {
      await notApproveBusiness(id, reason);
      toast.dismiss();
      toast.success("Not approved.");
      setNotApproveOpen(false);
      await refresh();
    } catch (e) {
      setNotApproveError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  const doResend = () => resender.resend(id, contact?.email);

  const header = (
    <div className="border border-[#D9E0F7] rounded-xl p-4 flex flex-wrap gap-4 justify-between items-start">
      <div className="flex flex-col gap-2 min-w-0">
        <h1 className="text-xl font-semibold break-words">{data.legalName}</h1>
        <p className="text-sm text-[#6B7280]">
          Registration · PAN {data.pan}
          {submitted ? ` · submitted ${timeAgo(submitted)}` : ""}
        </p>
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <StatusChip status={status} />
        {tone && <DueChip tone={tone} />}
        {mayResend && inviteOpen && (status === "invite_sent" || status === "in_progress") ? (
          <button
            type="button"
            className={smallButton}
            disabled={resender.busyId === id}
            onClick={doResend}
          >
            {resender.busyId === id ? "Resending…" : "Resend invite"}
          </button>
        ) : null}
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (status === "invite_sent" || status === "in_progress") {
    body = (
      <div className="flex flex-col gap-2 p-2">
        <h2 className="text-base font-semibold">
          {status === "in_progress" ? "The business is filling in its details" : "Waiting for the business"}
        </h2>
        <p className="text-sm">
          Invite sent to {contact?.name} · {contact?.email} · {timeAgo(invite?.sentAt ?? null)}
          {productNames ? ` · Products: ${productNames}` : ""}
        </p>
        <p className="text-sm text-[#6B7280]">
          Nothing to review yet. Once they submit, it moves to Waiting for review.
        </p>
      </div>
    );
  } else if (status === "approved" || status === "not_approved") {
    const ok = status === "approved";
    body = (
      <div className="flex flex-col gap-3 p-2">
        <h2 className="text-lg font-semibold">
          <span aria-hidden>{ok ? "✓ " : "✕ "}</span>
          {ok ? "Approved" : "Not approved"} {data.legalName}
        </h2>
        <p className="text-sm">
          {ok
            ? "Registered. Products are active."
            : data.application?.decisionReason ?? ""}
        </p>
        <div>
          <button
            type="button"
            className={outlineButton}
            onClick={() => navigate("/lead/contacts/corporate?tab=applications")}
          >
            Back to applications
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        {data.state === "fix_asked" && (
          <p className="text-sm border rounded-lg p-3 bg-[#FFF1D6]" role="status">
            Sent back to the business. It returns here when they resubmit.
          </p>
        )}
        {rows.length === 0 ? (
          <p className="p-2 text-sm">There are no items to review.</p>
        ) : (
          <ul className="flex flex-col divide-y border border-[#D9E0F7] rounded-xl">
            {rows.map((r) => {
              const doc = docById(r.documentId);
              const colour = ITEM_STATUS_COLOURS[r.status];
              const reasonLabel = reasons.find((x) => x.code === r.holdReasonCode)?.label;
              const label = itemLabel(r.itemKey, r.kind);
              return (
                <li key={r.id} className="p-4 flex flex-wrap gap-3 justify-between items-start">
                  <div className="flex flex-col gap-1.5 min-w-0 flex-1 basis-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-sm font-semibold">{label}</h2>
                      <span
                        className="py-[3px] px-2 rounded-md text-sm leading-4 font-medium"
                        style={{ backgroundColor: colour.bg, color: colour.text }}
                      >
                        {ITEM_STATUS_WORDS[r.status]}
                      </span>
                    </div>
                    {valuesFor(r).length > 0 && (
                      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
                        {valuesFor(r).map((v) => (
                          <div key={v.label} className="contents">
                            <dt className="text-[#4B5160]">{v.label}</dt>
                            <dd className="break-words">{v.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {doc &&
                      (doc.url ? (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-semibold text-blue-700 underline w-fit rounded-md sm:min-h-[40px] sm:inline-flex sm:items-center cursor-pointer hover:bg-[#E1EBFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
                        >
                          Check the document
                        </a>
                      ) : (
                        <span className="text-sm text-[#6B7280]">{doc.fileName}</span>
                      ))}
                    {r.status === "held" && (
                      <>
                        <p className="text-sm font-semibold text-[#8A5200]">
                          Waiting for the business to fix
                        </p>
                        <p className="text-sm">
                          Shown to the business: {r.reasonNote ?? reasonLabel}
                        </p>
                      </>
                    )}
                    {r.customerNote && (
                      <p className="text-sm text-[#4B5160]">The business says: {r.customerNote}</p>
                    )}
                    {r.staffNote && (
                      <p className="text-sm text-[#4B5160]">Staff note: {r.staffNote}</p>
                    )}
                  </div>
                  {mayReview && decidable && r.status === "open" && (
                    <div className="flex gap-2 md:w-[196px] md:shrink-0">
                      <button
                        type="button"
                        className={smallSolid}
                        disabled={busy !== null}
                        onClick={() => runItem(r.id, () => approveReviewItem(r.id))}
                      >
                        {busy === r.id ? "Approving…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        className={smallButton}
                        disabled={busy !== null}
                        onClick={() => {
                          setSendBackError("");
                          setSendBack(r);
                        }}
                      >
                        Send back
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {mayReview && decidable && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <span className="text-sm text-[#6B7280]">Reviewing as {session?.fullName}</span>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              {anyHeld && data.state !== "fix_asked" && (
                <span className="text-sm text-[#4B5160]">Resolve sent-back items first.</span>
              )}
              <button
                type="button"
                className={outlineButton}
                disabled={busy !== null}
                onClick={() => {
                  setNotApproveError("");
                  setNotApproveOpen(true);
                }}
              >
                Not approve
              </button>
              {data.state !== "fix_asked" && (
              <button
                type="button"
                className={primaryButton}
                disabled={busy !== null || anyHeld}
                onClick={() => setApproveAllOpen(true)}
              >
                {busy === "all" ? "Approving…" : "Approve all"}
              </button>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full overflow-auto p-2 font-inter">
      <p className="text-sm">
        <Link to="/lead/contacts/corporate?tab=applications" className={linkButton}>
          ‹ Applications
        </Link>
      </p>
      {header}
      {resender.link && (
        <InviteLinkPanel
          name={contact?.name}
          url={resender.link.url}
          onDismiss={resender.dismiss}
        />
      )}
      {body}
      <SendBackDialog
        itemLabel={sendBack ? itemLabel(sendBack.itemKey, sendBack.kind) : null}
        busy={busy === "sendback"}
        error={sendBackError}
        onCancel={() => setSendBack(null)}
        onConfirm={confirmSendBack}
      />
      <ApproveAllDialog
        open={approveAllOpen}
        name={data.legalName}
        onCancel={() => setApproveAllOpen(false)}
        onConfirm={() => {
          setApproveAllOpen(false);
          approveAll();
        }}
      />
      <NotApproveDialog
        open={notApproveOpen}
        name={data.legalName}
        busy={busy === "notapprove"}
        error={notApproveError}
        onCancel={() => setNotApproveOpen(false)}
        onConfirm={confirmNotApprove}
      />
    </div>
  );
}
